/**
 * Supabase Leads API Service
 *
 * Handles all CRUD operations for leads/customers
 */

import { supabase } from './client';
import type { Lead, LeadInsert, LeadUpdate } from './database.types';
import type { Customer } from 'src/types/customer';
import type { LeadStatus } from '../types';
import { notificationsApi } from './notifications';

// ============================================
// Type Mappings (DB <-> Frontend)
// ============================================

/**
 * Convert database row (snake_case) to frontend Customer (camelCase)
 * Handles missing columns gracefully
 */
const mapLeadToCustomer = (lead: any): Customer => ({
  id: lead.id,
  name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
  firstName: lead.first_name || '',
  lastName: lead.last_name || '',
  email: lead.email || '',
  phone: lead.phone || '',
  alternatePhone: lead.alternate_phone || undefined,

  // Primary Address
  address1: lead.address || '',
  city: lead.city || '',
  state: lead.state || 'WA',
  zipCode: lead.zip_code || '',
  country: 'USA',

  // Project Address
  projectAddress: lead.project_address || undefined,
  projectCity: lead.project_city || undefined,
  projectState: lead.project_state || undefined,
  projectZip: lead.project_zip || undefined,
  projectType: lead.project_type || undefined,
  projectNotes: lead.project_notes || undefined,

  // Linear Footage (include both field name variations for compatibility)
  numOfFt: lead.linear_footage || 0,
  linearFootage: lead.linear_footage || 0,
  numOfFtFront: lead.linear_footage_front || 0,
  linearFootageFront: lead.linear_footage_front || 0,
  numOfFtLeft: lead.linear_footage_left || 0,
  linearFootageLeft: lead.linear_footage_left || 0,
  numOfFtRight: lead.linear_footage_right || 0,
  linearFootageRight: lead.linear_footage_right || 0,
  numOfFtBack: lead.linear_footage_back || 0,
  linearFootageBack: lead.linear_footage_back || 0,

  // Lead Status & Categorization
  leadStatus: (lead.lead_status || 'warm') as LeadStatus,
  leadSource: lead.lead_source || undefined,
  customerCategory: lead.customer_category || undefined,
  referredBy: lead.referred_by || undefined,
  facebookLeadDate: lead.facebook_lead_date || undefined,

  // Financial
  estimatedValue: lead.estimated_value || undefined,
  homeValue: lead.home_value || undefined,
  depositAmount: lead.deposit_amount || undefined,
  depositPaid: lead.deposit_paid ?? false,
  depositDate: lead.deposit_date || undefined,
  balanceDue: lead.balance_due || undefined,
  paymentMethod: lead.payment_method || undefined,

  // Custom Pricing/Costs
  discountPercent: lead.discount_percent || 0,
  cashDiscount: lead.cash_discount || 0,
  taxRate: lead.tax_rate ?? 10,
  employeeCost: lead.employee_cost || 0,
  extraExpenses: lead.extra_expenses || 0,
  trackQty: lead.track_qty || 0,
  ledSections: lead.led_sections || 0,
  accessoriesCost: lead.accessories_cost || 0,
  shippingCost: lead.shipping_cost || 0,

  // Contact Preferences
  preferredContactMethod: lead.preferred_contact_method || 'phone',
  bestTimeToCall: lead.best_time_to_call || undefined,
  firstContactDate: lead.first_contact_date || undefined,
  lastContactDate: lead.last_contact_date || undefined,
  nextFollowUpDate: lead.next_follow_up_date || undefined,
  followUpNotes: lead.follow_up_notes || undefined,
  notes: lead.notes || lead.follow_up_notes || undefined,
  acceptsMarketing: lead.accepts_marketing ?? true,
  hasAcceptedMarketing: lead.accepts_marketing ?? true,

  // Installation
  estimatedInstallDate: lead.estimated_install_date || undefined,
  assignedCrew: lead.assigned_crew || undefined,
  completionDate: lead.completion_date || undefined,

  // Additional
  wantsLandscape: lead.wants_landscape ?? false,
  hasLandscape: lead.wants_landscape ?? false,
  googleDriveLinks: lead.google_drive_links || [],
  exhaustLeadSequence: lead.exhaust_lead_sequence || undefined,
  exhaustLeadNumber: lead.exhaust_lead_number || undefined,

  // In-person quote
  inPersonQuoteDate: lead.in_person_quote_date || undefined,
  inPersonQuoteTime: lead.in_person_quote_time || undefined,

  // Voice recording (from Atlas AI)
  voiceRecordingUrl: lead.voice_recording_url || undefined,
  voiceTranscriptUrl: lead.voice_transcript_url || undefined,
  voiceCallSummary: lead.voice_call_summary || undefined,
  voiceCallId: lead.voice_call_id || undefined,
  voiceCallDuration: lead.voice_call_duration || undefined,
  voiceCallSentiment: lead.voice_call_sentiment || undefined,

  // Dead lead tracking
  deadAt: lead.dead_at || undefined,
  deadFromStage: lead.dead_from_stage || undefined,
  deadReason: lead.dead_reason || undefined,

  // Metadata
  createdAt: lead.created_at,
  updatedAt: lead.updated_at,
});

/**
 * Convert frontend data to database insert format
 */
const mapCustomerToLead = (data: Partial<Customer>): LeadUpdate => {
  const result: LeadUpdate = {};

  // Only include fields that are defined
  // first_name and last_name are NOT NULL in the DB. Coerce empty/null to ''.
  if (data.firstName !== undefined) result.first_name = data.firstName || '';
  if (data.lastName !== undefined) result.last_name = data.lastName || '';
  if (data.email !== undefined) result.email = data.email || null;
  if (data.phone !== undefined) result.phone = data.phone || null;
  if (data.alternatePhone !== undefined) result.alternate_phone = data.alternatePhone || null;

  // Primary Address (accept both 'address' and 'address1' for flexibility)
  if (data.address1 !== undefined) result.address = data.address1 || null;
  if ((data as any).address !== undefined) result.address = (data as any).address || null;
  if (data.city !== undefined) result.city = data.city || null;
  if (data.state !== undefined) result.state = data.state || 'WA';
  if (data.zipCode !== undefined) result.zip_code = data.zipCode || null;

  // Project Address
  if (data.projectAddress !== undefined) result.project_address = data.projectAddress || null;
  if (data.projectCity !== undefined) result.project_city = data.projectCity || null;
  if (data.projectState !== undefined) result.project_state = data.projectState || null;
  if (data.projectZip !== undefined) result.project_zip = data.projectZip || null;
  if (data.projectType !== undefined) result.project_type = data.projectType as any || null;
  if (data.projectNotes !== undefined) result.project_notes = data.projectNotes || null;

  // Linear Footage
  if (data.linearFootage !== undefined) result.linear_footage = data.linearFootage || 0;
  if (data.numOfFt !== undefined) result.linear_footage = data.numOfFt || 0;
  if (data.linearFootageFront !== undefined) result.linear_footage_front = data.linearFootageFront || 0;
  if (data.linearFootageLeft !== undefined) result.linear_footage_left = data.linearFootageLeft || 0;
  if (data.linearFootageRight !== undefined) result.linear_footage_right = data.linearFootageRight || 0;
  if (data.linearFootageBack !== undefined) result.linear_footage_back = data.linearFootageBack || 0;

  // Lead Status & Categorization
  if (data.leadStatus !== undefined) result.lead_status = data.leadStatus as any;
  if (data.leadSource !== undefined) result.lead_source = data.leadSource as any || null;
  if (data.customerCategory !== undefined) result.customer_category = data.customerCategory as any || null;
  if (data.referredBy !== undefined) result.referred_by = data.referredBy || null;
  if (data.facebookLeadDate !== undefined) result.facebook_lead_date = data.facebookLeadDate || null;

  // Financial
  if (data.estimatedValue !== undefined) result.estimated_value = data.estimatedValue || null;
  if (data.homeValue !== undefined) result.home_value = data.homeValue || null;
  if (data.depositAmount !== undefined) result.deposit_amount = data.depositAmount || null;
  if (data.depositPaid !== undefined) result.deposit_paid = data.depositPaid;
  if (data.depositDate !== undefined) result.deposit_date = data.depositDate || null;
  if (data.balanceDue !== undefined) result.balance_due = data.balanceDue || null;
  if (data.paymentMethod !== undefined) result.payment_method = data.paymentMethod as any || null;

  // Custom Pricing/Costs
  if (data.discountPercent !== undefined) result.discount_percent = data.discountPercent || 0;
  if (data.cashDiscount !== undefined) result.cash_discount = data.cashDiscount || 0;
  if (data.taxRate !== undefined) result.tax_rate = data.taxRate || 10;
  if (data.employeeCost !== undefined) result.employee_cost = data.employeeCost || 0;
  if (data.extraExpenses !== undefined) result.extra_expenses = data.extraExpenses || 0;
  if (data.trackQty !== undefined) result.track_qty = data.trackQty || 0;
  if (data.ledSections !== undefined) result.led_sections = data.ledSections || 0;
  if (data.accessoriesCost !== undefined) result.accessories_cost = data.accessoriesCost || 0;
  if (data.shippingCost !== undefined) result.shipping_cost = data.shippingCost || 0;

  // Contact Preferences
  if (data.preferredContactMethod !== undefined) result.preferred_contact_method = data.preferredContactMethod as any || 'phone';
  if (data.bestTimeToCall !== undefined) result.best_time_to_call = data.bestTimeToCall || null;
  if (data.firstContactDate !== undefined) (result as any).first_contact_date = data.firstContactDate || null;
  if (data.lastContactDate !== undefined) result.last_contact_date = data.lastContactDate || null;
  if (data.nextFollowUpDate !== undefined) result.next_follow_up_date = data.nextFollowUpDate || null;
  if (data.followUpNotes !== undefined) result.follow_up_notes = data.followUpNotes || null;
  // Map 'notes' to 'project_notes' since 'notes' column doesn't exist in database
  if (data.notes !== undefined) result.project_notes = data.notes || null;
  if (data.acceptsMarketing !== undefined) result.accepts_marketing = data.acceptsMarketing;
  if (data.hasAcceptedMarketing !== undefined) result.accepts_marketing = data.hasAcceptedMarketing;

  // Installation
  if (data.estimatedInstallDate !== undefined) result.estimated_install_date = data.estimatedInstallDate || null;
  if (data.assignedCrew !== undefined) result.assigned_crew = data.assignedCrew as any || null;
  if (data.completionDate !== undefined) result.completion_date = data.completionDate || null;

  // Additional
  if (data.wantsLandscape !== undefined) result.wants_landscape = data.wantsLandscape;
  if (data.hasLandscape !== undefined) result.wants_landscape = data.hasLandscape;
  if (data.googleDriveLinks !== undefined) result.google_drive_links = data.googleDriveLinks || null;
  if (data.exhaustLeadSequence !== undefined) (result as any).exhaust_lead_sequence = data.exhaustLeadSequence || null;
  if (data.exhaustLeadNumber !== undefined) (result as any).exhaust_lead_number = data.exhaustLeadNumber || null;

  // In-person quote
  if (data.inPersonQuoteDate !== undefined) (result as any).in_person_quote_date = data.inPersonQuoteDate || null;
  if (data.inPersonQuoteTime !== undefined) (result as any).in_person_quote_time = data.inPersonQuoteTime || null;

  // Voice recording (from Atlas AI)
  if (data.voiceRecordingUrl !== undefined) (result as any).voice_recording_url = data.voiceRecordingUrl || null;
  if (data.voiceTranscriptUrl !== undefined) (result as any).voice_transcript_url = data.voiceTranscriptUrl || null;
  if (data.voiceCallSummary !== undefined) (result as any).voice_call_summary = data.voiceCallSummary || null;
  if (data.voiceCallId !== undefined) (result as any).voice_call_id = data.voiceCallId || null;
  if (data.voiceCallDuration !== undefined) (result as any).voice_call_duration = data.voiceCallDuration || null;
  if (data.voiceCallSentiment !== undefined) (result as any).voice_call_sentiment = data.voiceCallSentiment || null;

  // Dead lead tracking
  if (data.deadAt !== undefined) (result as any).dead_at = data.deadAt || null;
  if (data.deadFromStage !== undefined) (result as any).dead_from_stage = data.deadFromStage || null;
  if (data.deadReason !== undefined) (result as any).dead_reason = data.deadReason || null;

  return result;
};

// ============================================
// Leads API
// ============================================

interface GetLeadsParams {
  filters?: {
    query?: string;
    leadStatus?: LeadStatus;
    hasAcceptedMarketing?: boolean;
    isProspect?: boolean;
    isReturning?: boolean;
  };
  page?: number;
  rowsPerPage?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

interface GetLeadsResponse {
  data: Customer[];
  count: number;
}

class LeadsApi {
  /**
   * Get all leads with filtering, sorting, and pagination
   */
  async getAll(params: GetLeadsParams = {}): Promise<GetLeadsResponse> {
    const { filters, page = 0, rowsPerPage = 10, sortBy = 'updatedAt', sortDir = 'desc' } = params;

    let query = supabase.from('leads').select('*', { count: 'exact' });

    // Apply search filter (across multiple fields)
    if (filters?.query) {
      const searchTerm = filters.query.toLowerCase();
      query = query.or(
        `first_name.ilike.%${searchTerm}%,` +
        `last_name.ilike.%${searchTerm}%,` +
        `email.ilike.%${searchTerm}%,` +
        `phone.ilike.%${searchTerm}%,` +
        `city.ilike.%${searchTerm}%,` +
        `address.ilike.%${searchTerm}%`
      );
    }

    // Apply status filter
    // When filtering for 'closed', also include 'paid' for backwards compatibility
    if (filters?.leadStatus) {
      if (filters.leadStatus === 'closed') {
        query = query.in('lead_status', ['closed', 'paid']);
      } else {
        query = query.eq('lead_status', filters.leadStatus);
      }
    }

    // Apply marketing filter
    if (filters?.hasAcceptedMarketing !== undefined) {
      query = query.eq('accepts_marketing', filters.hasAcceptedMarketing);
    }

    // Convert sortBy from camelCase to snake_case
    // Map 'name' to 'first_name' since there's no 'name' column
    let sortColumn = sortBy;
    if (sortColumn === 'name') {
      sortColumn = 'first_name';
    } else {
      sortColumn = sortColumn
        .replace('updatedAt', 'updated_at')
        .replace('createdAt', 'created_at')
        .replace('firstName', 'first_name')
        .replace('lastName', 'last_name')
        .replace('leadStatus', 'lead_status')
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase();
    }

    // Apply sorting
    query = query.order(sortColumn, { ascending: sortDir === 'asc' });

    // Apply pagination
    const from = page * rowsPerPage;
    const to = from + rowsPerPage - 1;
    query = query.range(from, to);

    const { data, count, error } = await query;

    if (error) {
      // console.error('Failed to fetch leads:', error);
      throw new Error(`Failed to fetch leads: ${error.message}`);
    }

    return {
      data: (data || []).map((row: any) => mapLeadToCustomer(row)),
      count: count || 0,
    };
  }

  /**
   * Get leads for the customers API (alias for getAll)
   */
  async getCustomers(params: GetLeadsParams = {}): Promise<GetLeadsResponse> {
    return this.getAll(params);
  }

  /**
   * Get a single lead by ID
   */
  async getById(id: string): Promise<Customer> {
    // console.log('leadsApi.getById called with id:', id);

    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('id', id)
      .single();

    // console.log('Supabase getById response:', { data, error });

    if (error) {
      // console.error('Failed to fetch lead:', error);
      throw new Error(`Failed to fetch lead: ${error.message}`);
    }

    const customer = mapLeadToCustomer(data);
    // console.log('Mapped customer:', customer);
    return customer;
  }

  /**
   * Get a single customer by ID (alias for getById)
   */
  async getCustomer(id: string): Promise<Customer> {
    return this.getById(id);
  }

  /**
   * Create a new lead
   */
  async create(data: Partial<Customer>): Promise<Customer> {
    const leadData = mapCustomerToLead(data);

    // Ensure required fields
    if (!leadData.first_name) {
      throw new Error('First name is required');
    }
    if (!leadData.last_name) {
      leadData.last_name = '';
    }
    if (!leadData.lead_status) {
      leadData.lead_status = 'warm';
    }

    // Pass through every field mapCustomerToLead produced — the previous
    // explicit whitelist silently dropped columns like in_person_quote_date,
    // first_contact_date, voice_*, etc.
    const insertData = {
      ...leadData,
      first_name: leadData.first_name,
      last_name: leadData.last_name || '',
      state: leadData.state || 'WA',
      lead_status: leadData.lead_status || 'warm',
      preferred_contact_method: leadData.preferred_contact_method || 'phone',
      accepts_marketing: leadData.accepts_marketing ?? true,
    } as LeadInsert;

    console.log('[leadsApi.create] insertData being sent:', insertData);

    const { data: result, error } = await supabase
      .from('leads')
      .insert(insertData as any)
      .select()
      .single();

    console.log('[leadsApi.create] response:', { result, error });

    if (error) {
      console.error('[leadsApi.create] Supabase error:', error);
      throw new Error(`Failed to create lead: ${error.message}`);
    }
    if (!result || !(result as any).id) {
      console.error('[leadsApi.create] Insert returned no row. Likely RLS blocked the SELECT after insert.');
      throw new Error('Lead was not saved (no row returned). Check Supabase RLS policies for SELECT on leads.');
    }

    const customer = mapLeadToCustomer(result as Lead);

    // Create notification for Facebook leads
    if (leadData.lead_source === 'facebook' || leadData.lead_status === 'facebook_new') {
      const leadName = `${customer.firstName} ${customer.lastName}`.trim() || 'Unknown';
      notificationsApi.createFacebookLeadNotification(leadName, customer.id).catch((err) => {
        // console.error('Failed to create Facebook lead notification:', err);
      });
    }

    return customer;
  }

  /**
   * Update an existing lead
   */
  async update(id: string, data: Partial<Customer>): Promise<Customer> {
    const leadData = mapCustomerToLead(data);
    console.log('[leadsApi.update] id:', id, 'leadData:', leadData);

    const { data: result, error } = await (supabase
      .from('leads')
      .update(leadData as any)
      .eq('id', id)
      .select()
      .single() as any);

    console.log('[leadsApi.update] response:', { result, error });

    if (error) {
      console.error('[leadsApi.update] Supabase error:', error);
      throw new Error(`Failed to update lead: ${error.message}`);
    }
    if (!result) {
      throw new Error('Update returned no row. Either the id was wrong or RLS blocked the SELECT.');
    }

    return mapLeadToCustomer(result as Lead);
  }

  /**
   * Delete a lead
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('leads').delete().eq('id', id);

    if (error) {
      // console.error('Failed to delete lead:', error);
      throw new Error(`Failed to delete lead: ${error.message}`);
    }
  }

  /**
   * Get leads by status
   */
  async getByStatus(status: LeadStatus): Promise<Customer[]> {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('lead_status', status)
      .order('updated_at', { ascending: false });

    if (error) {
      // console.error('Failed to fetch leads by status:', error);
      throw new Error(`Failed to fetch leads by status: ${error.message}`);
    }

    return (data || []).map(mapLeadToCustomer);
  }

  /**
   * Update lead status
   */
  async updateStatus(id: string, status: LeadStatus): Promise<Customer> {
    return this.update(id, { leadStatus: status });
  }

  /**
   * Log a contact with a lead
   */
  async logContact(
    id: string,
    followUpDate?: string,
    notes?: string
  ): Promise<Customer> {
    return this.update(id, {
      lastContactDate: new Date().toISOString(),
      nextFollowUpDate: followUpDate,
      followUpNotes: notes,
    });
  }

  /**
   * Get recent Facebook leads (lead_status = 'facebook_new')
   */
  async getFacebookLeads(limit: number = 2): Promise<Customer[]> {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .eq('lead_status', 'facebook_new')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`Failed to fetch Facebook leads: ${error.message}`);
    }

    return (data || []).map(mapLeadToCustomer);
  }

  /**
   * Get count of Facebook leads (lead_status = 'facebook_new')
   */
  async getFacebookLeadsCount(): Promise<number> {
    const { count, error } = await supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
      .eq('lead_status', 'facebook_new');

    if (error) {
      throw new Error(`Failed to count Facebook leads: ${error.message}`);
    }

    return count || 0;
  }

  /**
   * Search leads across multiple fields
   */
  async search(query: string): Promise<Customer[]> {
    const searchTerm = query.toLowerCase();

    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .or(
        `first_name.ilike.%${searchTerm}%,` +
        `last_name.ilike.%${searchTerm}%,` +
        `email.ilike.%${searchTerm}%,` +
        `phone.ilike.%${searchTerm}%,` +
        `city.ilike.%${searchTerm}%`
      )
      .order('updated_at', { ascending: false })
      .limit(50);

    if (error) {
      // console.error('Failed to search leads:', error);
      throw new Error(`Failed to search leads: ${error.message}`);
    }

    return (data || []).map(mapLeadToCustomer);
  }
}

export const leadsApi = new LeadsApi();

// ============================================
// Real-time Subscriptions
// ============================================

export type LeadChangeCallback = (payload: {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: Customer | null;
  old: { id: string } | null;
}) => void;

/**
 * Subscribe to real-time lead changes
 * Returns an unsubscribe function
 */
export const subscribeToLeads = (callback: LeadChangeCallback): (() => void) => {
  const channel = supabase
    .channel('leads-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'leads',
      },
      (payload) => {
        const newLead = payload.new && Object.keys(payload.new).length > 0
          ? mapLeadToCustomer(payload.new as any)
          : null;
        const oldLead = payload.old && (payload.old as any).id
          ? { id: (payload.old as any).id }
          : null;

        callback({
          eventType: payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
          new: newLead,
          old: oldLead,
        });
      }
    )
    .subscribe();

  // Return unsubscribe function
  return () => {
    supabase.removeChannel(channel);
  };
};

/**
 * Subscribe to real-time notification changes
 * Returns an unsubscribe function
 */
export const subscribeToNotifications = (callback: (payload: any) => void): (() => void) => {
  const channel = supabase
    .channel('notifications-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notifications',
      },
      callback
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

// Also export as customersApi for backwards compatibility
export const customersApi = {
  getCustomers: (params: GetLeadsParams) => leadsApi.getCustomers(params),
  getCustomer: (id: string) => leadsApi.getCustomer(id),
};
